import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { profilesCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { TextareaField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { userModelApi } from '../../api/memory'
import { useMutationToast } from '../../hooks/useMutationToast'
import type { UserModelResponse } from '../../types/api'
import { formatDateTime } from '../../utils/time'
import styles from './UserModelSection.module.css'

/**
 * One member's user model: the structured observations workspace agents have
 * recorded about them. Every member can read and curate their own; managers
 * can inspect any member's from the members tab.
 *
 * `username: null` targets the caller's own model via the `/user-model`
 * routes (write access); a username targets the manager-inspection route
 * (read + delete, no adding on another's behalf).
 */
export function UserModelSection({
  wsId,
  username = null,
}: {
  wsId: string
  username?: string | null
}) {
  const qc = useQueryClient()
  const [deleting, setDeleting] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const formId = useId()

  const queryKey = ['user-model', wsId, username ?? 'me']

  const { data, isLoading, error, refetch } = useQuery<UserModelResponse>({
    queryKey,
    queryFn: () =>
      username
        ? userModelApi.getForMember(wsId, username)
        : userModelApi.getMine(wsId),
    enabled: !!wsId,
    staleTime: 15_000,
    retry: false,
  })
  const showSkeleton = useSkeletonDelay(isLoading)

  function invalidate() {
    void qc.invalidateQueries({ queryKey })
  }

  const addMut = useMutationToast({
    mutationFn: () => userModelApi.addMine(wsId, draft.trim()),
    successMessage: profilesCopy.userModel.added,
    errorMessage: profilesCopy.userModel.addFailed,
    onSuccess: () => {
      setAdding(false)
      setDraft('')
      invalidate()
    },
  })

  const deleteMut = useMutationToast({
    mutationFn: (id: string) => userModelApi.remove(wsId, id),
    successMessage: profilesCopy.userModel.deleted,
    errorMessage: profilesCopy.userModel.deleteFailed,
    onSuccess: () => {
      setDeleting(null)
      invalidate()
    },
  })

  const observations = data?.observations ?? []
  // Only the caller's own model takes new observations.
  const canAdd = username === null
  const addObservation = (
    <Button variant="ghost" onClick={() => setAdding(true)}>
      <Icon name="plus" size="icon-embedded" />
      {profilesCopy.userModel.add}
    </Button>
  )

  return (
    <Stack gap="space-12">
      {error && data && (
        <Alert tone="warning">
          <Inline gap="space-8" align="baseline">
            <Text>{profilesCopy.refreshUserModelFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {profilesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      )}
      {isLoading ? (
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      ) : error && !data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{profilesCopy.userModel.loadFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {profilesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : observations.length === 0 && !(canAdd && adding) ? (
        <EmptyState
          variant="inline"
          description={profilesCopy.userModel.empty}
          action={canAdd ? addObservation : undefined}
        />
      ) : (
        <RowList>
          {observations.map((o) => (
            <RowListItem key={o.id}>
              <Text tone="text-sub" className={styles.content}>
                {o.content}
              </Text>
              <Text variant="caption" tone="text-mute" numeric>
                {o.created_by_agent
                  ? profilesCopy.userModel.byAgent(o.created_by_agent)
                  : ''}
                {formatDateTime(o.created_at)}
              </Text>
              <Button variant="ghost" onClick={() => setDeleting(o.id)}>
                {profilesCopy.userModel.delete}
              </Button>
            </RowListItem>
          ))}
          {canAdd &&
            (adding ? (
              <RowListItem className={styles.form}>
                <form
                  onSubmit={(ev) => {
                    ev.preventDefault()
                    if (draft.trim()) addMut.mutate()
                  }}
                >
                  <Stack gap="space-12">
                    <TextareaField
                      id={`${formId}-observation`}
                      label={profilesCopy.newObservation}
                      value={draft}
                      onValue={setDraft}
                      textareaProps={{ rows: 3, autoFocus: true }}
                    />
                    <Inline gap="space-8">
                      <Button
                        type="submit"
                        disabled={!draft.trim()}
                        loading={addMut.isPending}
                      >
                        {profilesCopy.userModel.save}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAdding(false)}
                      >
                        {profilesCopy.userModel.cancel}
                      </Button>
                    </Inline>
                  </Stack>
                </form>
              </RowListItem>
            ) : (
              <RowListItem>{addObservation}</RowListItem>
            ))}
        </RowList>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={profilesCopy.userModel.deleteTitle}
        tone="danger"
        description={profilesCopy.userModel.deleteDescription}
        confirmLabel={profilesCopy.userModel.deleteConfirm}
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (deleting) deleteMut.mutate(deleting)
        }}
        onClose={() => setDeleting(null)}
      />
    </Stack>
  )
}
